import {
  AloftArea,
  AloftPrice,
  CraftsmanCostProps,
  ORDER_FEATURES,
  getCraftsmanCost,
  isDefined,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectCompSequence } from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectCostProps } from './selectCostProps.ts';

const calculateShedCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
) => {
  const improvementsForTransaction = improvements[id];
  const identification = propertyIdentification[id];

  if (!improvementsForTransaction || !identification) return undefined;

  const { shed } = improvementsForTransaction;
  const {
    address: { PostalCode: zipCode },
  } = identification;

  if (!shed || !shed.area || isUndefined(shed.quality) || isUndefined(zipCode)) {
    return undefined;
  }

  const calculator = getCraftsmanCost(costProps);

  return calculator.getShedCost({
    hasFeature: ORDER_FEATURES.shed.hasFeature(improvementsForTransaction),
    sqft: AloftArea.schema.parse(shed.area),
    quality: shed.quality,
    zipCode,
  });
};

const getTotalOrBreakdownShedCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
  type: 'totalCost' | 'zipOnly',
) => {
  const costOutput = calculateShedCost(costProps, improvements, propertyIdentification, id);
  if (!costOutput) return AloftPrice.zero;

  return type === 'totalCost' ? costOutput.totalCost : costOutput.multiplierBreakdown[type];
};

const getTotalShedCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
) => getTotalOrBreakdownShedCost(costProps, improvements, propertyIdentification, id, 'totalCost');

export const selectShedCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state, id: string) => id,
  ],
  getTotalShedCost,
);

export const selectZipOnlyShedCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state, id: string) => id,
  ],
  (costProps, improvements, propertyIdentification, id) =>
    getTotalOrBreakdownShedCost(costProps, improvements, propertyIdentification, id, 'zipOnly'),
);

export const selectShedCosts = createSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    selectCompSequence,
  ],
  (costProps, improvements, propertyIdentification, compSequence) =>
    ['subject', ...compSequence].reduce((acc: Record<string, AloftPrice>, propID) => {
      const cost = getTotalShedCost(costProps, improvements, propertyIdentification, propID);
      if (isDefined(cost)) acc[propID] = cost;
      return acc;
    }, {}),
);

export const selectShedCostDisplay = createSelector([selectShedCost], (shedCost) =>
  shedCost?.toString(true),
);
